import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import httpServices from '../../../services/httpServices';
import DashboardLayout from '../../common/DashboardLayout';
import './customTrainingManagement.css';

const CustomTrainingManagement = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await httpServices.get('/admin/custom-training/courses');

      if (response.success) {
        setCourses(response.courses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const toggleActivation = async (courseId, currentStatus) => {
    try {
      const response = await httpServices.post(
        `/admin/custom-training/courses/${courseId}/activate`,
        { isActive: !currentStatus }
      );

      if (response.success) {
        // Update local state
        setCourses(courses.map(course =>
          course.id === courseId
            ? { ...course, isActive: !currentStatus }
            : course
        ));
      }
    } catch (error) {
      console.error('Error toggling activation:', error);
      alert('Failed to update course status');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await httpServices.delete(
        `/admin/custom-training/courses/${courseId}`
      );

      if (response.success) {
        setCourses(courses.filter(course => course.id !== courseId));
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course');
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="custom-training-management">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading courses...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="custom-training-management">
        <div className="page-header">
          <div className="header-content">
            <h1>📚 Custom Training Management</h1>
            <p className="subtitle">Manage custom PDF training courses and quizzes</p>
          </div>
          <Link to="/admin/custom-training/import" className="btn btn-outline" style={{ marginRight: 8 }}>
            <i className="bx bx-file"></i> Build from PDF
          </Link>
          <Link to="/admin/custom-training/create" className="btn btn-primary">
            <i className="bx bx-plus"></i> Create New Course
          </Link>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {courses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📖</div>
            <h3>No Custom Training Courses Yet</h3>
            <p>Create your first custom training course to get started</p>
            <Link to="/admin/custom-training/create" className="btn btn-primary">
              Create First Course
            </Link>
          </div>
        ) : (
          <div className="courses-grid">
            {courses.map(course => (
              <div key={course.id} className="course-card">
                <div className="course-header">
                  <div className="course-status">
                    {course.isActive ? (
                      <span className="badge badge-success">Active</span>
                    ) : (
                      <span className="badge badge-secondary">Inactive</span>
                    )}
                    {course.isRequired && (
                      <span className="badge badge-warning">Required</span>
                    )}
                  </div>
                  <div className="course-actions">
                    <button
                      className="btn-icon"
                      onClick={() => toggleActivation(course.id, course.isActive)}
                      title={course.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <i className={`bx ${course.isActive ? 'bx-toggle-right' : 'bx-toggle-left'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="course-body">
                  <h3>{course.title}</h3>
                  {course.description && (
                    <p className="course-description">{course.description}</p>
                  )}

                  <div className="course-meta">
                    {course.hours && (
                      <div className="meta-item">
                        <i className="bx bx-time"></i>
                        <span>{course.hours} hours</span>
                      </div>
                    )}
                    <div className="meta-item">
                      <i className="bx bx-calendar"></i>
                      <span>Expires in {course.expirationMonths} months</span>
                    </div>
                  </div>

                  <div className="course-info">
                    {course.pdfFileName ? (
                      <div className="info-item success">
                        <i className="bx bx-file-blank"></i>
                        <span>PDF: {course.pdfFileName}</span>
                      </div>
                    ) : (
                      <div className="info-item warning">
                        <i className="bx bx-error"></i>
                        <span>No PDF uploaded</span>
                      </div>
                    )}
                    {course.quizId ? (
                      <div className="info-item success">
                        <i className="bx bx-check-circle"></i>
                        <span>Quiz configured</span>
                      </div>
                    ) : (
                      <div className="info-item warning">
                        <i className="bx bx-error"></i>
                        <span>No quiz</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="course-footer">
                  <Link
                    to={`/admin/custom-training/edit/${course.id}`}
                    className="btn btn-sm btn-outline"
                  >
                    <i className="bx bx-edit"></i> Edit
                  </Link>
                  <button
                    onClick={() => deleteCourse(course.id)}
                    className="btn btn-sm btn-danger"
                  >
                    <i className="bx bx-trash"></i> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomTrainingManagement;
